import LazyLoader from 'Components/LazyLoader';
import { useInfiniteScroll } from 'Hooks/scroll/useInfiniteScroll';
import useForwardedRef from 'Hooks/useForwardedRef/useForwardedRef';
import React, { forwardRef, ForwardRefRenderFunction } from 'react';
import { hasRenderableChildren } from 'Utils/hasRenderableChildren';
import { Styled } from './index.styles';
import { InfiniteListProps } from './index.types';

/**
 * InfiniteList is a scrollable container that supports infinite scrolling.
 * It dynamically loads more items when the user scrolls near the bottom of the list.
 *
 * This component can be used as a styled component, by using `style(InfiniteList)`.
 *
 * @example
 * ```tsx
 * <InfiniteList
 *   isLoading={isLoading}
 *   hasError={hasError}
 *   onScroll={loadMoreItems}
 *   emptyState={<div>No items available</div>}
 *   errorState={<div>Failed to load items</div>}
 * >
 *   {items.map((item, index) => (
 *     <div key={index}>{item}</div>
 *   ))}
 * </InfiniteList>
 * ```
 */
const InfiniteList: ForwardRefRenderFunction<
  HTMLDivElement,
  InfiniteListProps
> = (
  {
    scrollOffset = 200,
    isLoading,
    emptyState,
    errorState,
    hasError,
    onScroll,
    children,
    style,
    className,
    testId,
  },
  ref
) => {
  const [setContainerRef, containerRef] = useForwardedRef(ref);
  const hasChildren = hasRenderableChildren(children);

  const loadMoreOnScroll = useInfiniteScroll(containerRef, {
    scrollOffset,
    isLoading,
    onLoadMore: onScroll,
    hasContent: hasChildren,
    contentDependency: children,
  });

  const handleScroll = React.useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      window.dispatchEvent(new Event('conversation-list-scroll'));
      loadMoreOnScroll(e);
    },
    [loadMoreOnScroll]
  );

  /**
   * If there is an error, show the error state.
   * This takes precedence over everything else.
   */
  if (hasError && errorState) {
    return (
      <Styled.Container
        {...{ style, className }}
        ref={setContainerRef}
        data-testid={testId}
      >
        {errorState}
      </Styled.Container>
    );
  }

  /**
   * If there is no error, and there are items, show them.
   */
  if (hasChildren) {
    return (
      <Styled.Container
        {...{ style, className }}
        ref={setContainerRef}
        onScroll={handleScroll}
        data-testid={testId}
      >
        {children}
        {isLoading && (
          <Styled.InlineLoaderContainer>
            <LazyLoader size="small" />
          </Styled.InlineLoaderContainer>
        )}
      </Styled.Container>
    );
  }

  /**
   * This loading state should be shown only if there are no items, and the data is loading.
   */
  if (isLoading) {
    return (
      <Styled.Container
        {...{ style, className }}
        ref={setContainerRef}
        data-testid={testId}
      >
        <LazyLoader />
      </Styled.Container>
    );
  }

  /**
   * If there are no items, and there is no loading state, show the empty state.
   */
  if (emptyState) {
    return (
      <Styled.Container
        {...{ style, className }}
        $noScroll
        ref={setContainerRef}
        data-testid={testId}
      >
        {emptyState}
      </Styled.Container>
    );
  }

  /**
   * If everything fails, just show the container by itself.
   */
  return (
    <Styled.Container
      {...{ style, className }}
      ref={setContainerRef}
      data-testid={testId}
    />
  );
};

export default forwardRef<HTMLDivElement, InfiniteListProps>(InfiniteList);
